import { Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import type {
  OscarLegRole,
  OscarProtectionProvenance,
  OscarProtectionState,
  OscarReceiptLeg,
  OscarReceiptLegState,
  OscarReceiptState,
  OscarSequenceReceipt,
} from '@/types/kalshi';

/**
 * #302 — the durable Oscar sequence receipt.
 *
 * Presentation only. Every field comes from the relay's projection over the
 * trades audit; this component derives nothing about what happened at the
 * venue and never upgrades a state the relay refused to call complete.
 */

/**
 * Render an exact fixed-point dollar STRING as cents, by shifting the decimal
 * point textually.
 *
 * `Number('0.615') * 100` is 61.49999999999999, and every float path to cents
 * has a case like it. The audit already holds the exact decimal the order was
 * placed at, so the only correct transform is a string one: move the point two
 * places right, drop the padding, keep whatever real precision remains.
 * Returns null (render '—') for anything unparseable rather than guessing.
 */
export function formatPriceDollarsAsCents(priceDollars: string | null): string | null {
  if (priceDollars == null) return null;
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(priceDollars.trim());
  if (!match) return null;
  const [, sign, intPart, fracRaw = ''] = match;
  const frac = fracRaw.length >= 2 ? fracRaw : (fracRaw + '00').slice(0, 2);
  const whole = (intPart + frac.slice(0, 2)).replace(/^0+(?=\d)/, '');
  const rest = frac.slice(2).replace(/0+$/, '');
  return `${sign}${whole}${rest ? `.${rest}` : ''}¢`;
}

const LEG_LABEL: Record<OscarLegRole, string> = {
  cancel: 'Release',
  kill: 'Kill blocker',
  take: 'Take',
  blocker: 'Blocker',
};

const LEG_STATE_LABEL: Record<OscarReceiptLegState, string> = {
  placed: 'Placed',
  already_placed: 'Already placed',
  not_requested: 'Not requested',
  skipped: 'Skipped',
  skipped_or_not_requested: 'Skipped or not requested',
  rejected: 'Rejected',
  canceled_before_replace: 'Canceled before replace',
  recovery_pending: 'Recovery pending',
  unknown: 'Unknown',
};

const SEQUENCE_STATE_LABEL: Record<OscarReceiptState, string> = {
  complete: 'Complete',
  partial: 'Partial',
  recovery_pending: 'Recovery pending',
  rejected: 'Rejected',
  unknown: 'Unknown',
};

const SEQUENCE_STATE_CLASS: Record<OscarReceiptState, string> = {
  complete: 'bg-positive/10 text-positive',
  partial: 'bg-amber-500/10 text-amber-500',
  recovery_pending: 'bg-amber-500/10 text-amber-500',
  rejected: 'bg-negative/10 text-negative',
  unknown: 'bg-amber-500/10 text-amber-500',
};

const OP_LABEL: Record<OscarSequenceReceipt['op'], string> = {
  overtake: 'Overtake',
  release_overtake: 'Release & Overtake',
  unknown: 'Unrecognized operation',
};

function timeLabel(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function LegRow({ leg }: { leg: OscarReceiptLeg }) {
  const price = formatPriceDollarsAsCents(leg.priceDollars);
  return (
    <div data-testid={`oscar-receipt-leg-${leg.role}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-muted-foreground shrink-0">{LEG_LABEL[leg.role]}</span>
        <span className="text-foreground text-right font-mono text-[11px] leading-tight">
          {LEG_STATE_LABEL[leg.state]}
          {leg.side ? ` · ${leg.side.toUpperCase()}` : ''}
          {leg.count != null ? ` · ${leg.count}ct` : ''}
          {price ? ` @ ${price}` : ''}
          {' · '}
          {/* Never invent an id: an accepted leg the relay could not tie to a
              venue order reads `unknown`, which is the trader's cue to look. */}
          <span className="text-muted-foreground">{leg.venueOrderId ?? 'unknown'}</span>
        </span>
      </div>
      {/* WHY the leg ended up in that state — the relay's own recorded reason.
          On a partial or recovery-pending sequence this is the single most
          actionable field on the card, and it already crosses the wire, so
          not rendering it is pure exposure for no benefit. */}
      {leg.message && (
        <div
          data-testid={`oscar-receipt-leg-${leg.role}-message`}
          // #594 — the relay's recorded reason is the card's most actionable
          // field on a partial/pending sequence; it WRAPS in its own
          // full-width region instead of truncating into a hover-only title.
          className="text-muted-foreground break-words text-[10px] leading-tight"
        >
          {leg.message}
        </div>
      )}
    </div>
  );
}

/** #640 — glanceable protection lifecycle for release_overtake receipts.
 *  Read-only mirror of the relay's own verdict: states, evidence class, and
 *  guidance all arrive on the wire; nothing here upgrades, infers, or adds a
 *  control. The two states meaning "your level is UNPROTECTED" take the
 *  negative treatment — that is the fact a glance must not miss. */
const PROTECTION_STATE_LABEL: Record<OscarProtectionState, string> = {
  retained: 'Retained',
  restored: 'Restored',
  replacement_pending: 'Replacement pending',
  replacement_failed: 'Unprotected · replacement failed',
  released: 'Unprotected · released',
  unresolved: 'Unresolved',
};

const PROTECTION_STATE_CLASS: Record<OscarProtectionState, string> = {
  retained: 'bg-positive/10 text-positive',
  restored: 'bg-positive/10 text-positive',
  replacement_pending: 'bg-amber-500/10 text-amber-500',
  replacement_failed: 'bg-negative/10 text-negative',
  released: 'bg-negative/10 text-negative',
  unresolved: 'bg-amber-500/10 text-amber-500',
};

/** A state string this build does not recognize (newer relay) renders as an
 *  explicit amber "Unrecognized" — never silently mapped onto a known state
 *  and never a crash. */
function protectionPresentation(state: string): { label: string; className: string } {
  const known = (PROTECTION_STATE_LABEL as Record<string, string>)[state];
  return known
    ? { label: known, className: (PROTECTION_STATE_CLASS as Record<string, string>)[state] }
    : { label: 'Unrecognized state', className: 'bg-amber-500/10 text-amber-500' };
}

function ProtectionRow({ protection }: { protection: OscarProtectionProvenance }) {
  if (protection == null || typeof protection.state !== 'string') return null;
  const { label, className } = protectionPresentation(protection.state);
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground shrink-0">Protection</span>
      <span
        data-testid="oscar-receipt-protection-state"
        className={cn('rounded px-1.5 py-0.5 text-right font-semibold', className)}
      >
        {label}
      </span>
    </div>
  );
}

/** #640/#594 — ONE guidance region. Protection guidance and the sequence's
 *  own guidance used to render as two stacked amber blocks; on a failed
 *  release that doubled the card's tail with two 'verify first' paragraphs.
 *  They now share one container: identical lines dedupe, distinct lines stack
 *  compactly, malformed non-string values render nothing, and every line
 *  stays full-width wrapping content — visible without hover. */
function guidanceLines(receipt: OscarSequenceReceipt): { key: string; text: string }[] {
  const lines: { key: string; text: string }[] = [];
  const protectionGuidance = receipt.protection?.guidance;
  if (typeof protectionGuidance === 'string' && protectionGuidance.trim() !== '') {
    lines.push({ key: 'protection', text: protectionGuidance });
  }
  if (
    typeof receipt.guidance === 'string' &&
    receipt.guidance.trim() !== '' &&
    receipt.guidance !== protectionGuidance
  ) {
    lines.push({ key: 'sequence', text: receipt.guidance });
  }
  return lines;
}

export interface OscarSequenceReceiptProps {
  receipt: OscarSequenceReceipt | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

export function OscarSequenceReceipt({
  receipt,
  loading,
  error,
  onRefresh,
}: OscarSequenceReceiptProps) {
  return (
    <div
      data-testid="oscar-receipt"
      className="border-border space-y-1.5 rounded-lg border px-3 py-2 text-xs"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted-foreground font-semibold uppercase tracking-wide">
          Overtake receipt
        </span>
        <div className="flex items-center gap-2">
          {loading && (
            <Loader2 data-testid="oscar-receipt-loading" size={11} className="animate-spin" />
          )}
          <button
            type="button"
            onClick={onRefresh}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          >
            <RefreshCw size={11} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="text-negative">{error}</div>}

      {!receipt && !error && (
        <div className="text-muted-foreground">No overtake sequence recorded for this market.</div>
      )}

      {receipt && (
        <>
          <div className="flex items-center justify-between gap-2">
            <span data-testid="oscar-receipt-op" className="text-foreground">
              {OP_LABEL[receipt.op]}
            </span>
            <span
              data-testid="oscar-receipt-state"
              className={cn('rounded px-1.5 py-0.5 font-semibold', SEQUENCE_STATE_CLASS[receipt.state])}
            >
              {SEQUENCE_STATE_LABEL[receipt.state]}
            </span>
          </div>

          <div className="text-muted-foreground flex items-center justify-between gap-2">
            <span className="font-mono text-[11px]">{receipt.ticker}</span>
            <span>
              <span data-testid="oscar-receipt-take-side">
                {receipt.takeSide ? `Take ${receipt.takeSide.toUpperCase()}` : 'Take side unknown'}
              </span>
              {' · '}
              {timeLabel(receipt.sequenceStartedAt)}
            </span>
          </div>

          <div className="space-y-0.5">
            {receipt.legs.map((leg) => (
              <LegRow key={leg.role} leg={leg} />
            ))}
          </div>

          {/* #640 — absent on legacy receipts and plain overtakes: render
              NOTHING rather than a guessed state (preserves pre-field output
              byte-for-byte). */}
          {receipt.protection != null && <ProtectionRow protection={receipt.protection} />}

          {guidanceLines(receipt).length > 0 && (
            <div
              data-testid="oscar-receipt-guidance"
              className="bg-amber-500/10 text-amber-500 space-y-1 rounded px-2 py-1"
            >
              {guidanceLines(receipt).map((line) => (
                <div
                  key={line.key}
                  data-testid={
                    line.key === 'protection' ? 'oscar-receipt-protection-guidance' : undefined
                  }
                >
                  {line.text}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
